/**
 * FANTASY CONTEST CHAT - Smack Talk! 🗣️🔥
 * 
 * Chat panel for fantasy contest participants to trash talk each other.
 * Shows rank badges, scores, and time stamps.
 */

import { useState, useEffect, useRef } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { 
  MessageCircle, 
  Send, 
  Trophy,
  Loader,
  ChevronDown
} from 'lucide-react'
import { API_BASE_URL } from '../constants'
import { useAuthStore } from '../store/authStore'

interface ChatMessage {
  id: number
  contest_id: number
  user_id: number | null
  wallet_address: string | null
  username: string
  message: string
  entry_id: number | null
  entry_rank: number | null
  entry_score: number | null
  reactions: any[]
  created_at: string
  timeAgo: string
}

interface FantasyChatProps {
  contestId: number
  contestStatus: string
}

export default function FantasyChat({ contestId, contestStatus }: FantasyChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [isMinimized, setIsMinimized] = useState(false)
  const { user } = useAuthStore()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Fetch messages
  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/fantasy/contests/${contestId}/chat`)
      setMessages(response.data.messages || [])
    } catch (error) {
      console.error('Failed to fetch chat:', error)
    } finally {
      setLoading(false)
    }
  }

  // Poll for new messages every 5 seconds
  useEffect(() => {
    fetchMessages()
    const interval = setInterval(fetchMessages, 5000)
    return () => clearInterval(interval)
  }, [contestId])

  // Send message
  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!user) {
      toast.error('Please log in to chat')
      return
    }

    if (!newMessage.trim()) {
      return
    }

    try {
      setSending(true)
      
      await axios.post(`${API_BASE_URL}/api/fantasy/contests/${contestId}/chat`, {
        message: newMessage.trim(),
        userId: user.id,
        displayName: user.username || `User ${user.id}`
      })

      setNewMessage('')
      fetchMessages() // Refresh to show new message
      
    } catch (error: any) {
      console.error('Failed to send message:', error)
      toast.error(error.response?.data?.error || 'Failed to send message')
    } finally {
      setSending(false)
    }
  }

  // Get rank badge color
  const getRankColor = (rank: number | null) => {
    if (!rank) return 'text-gray-400'
    if (rank === 1) return 'text-yellow-400'
    if (rank === 2) return 'text-gray-300'
    if (rank === 3) return 'text-amber-600'
    if (rank <= 10) return 'text-emerald-400'
    return 'text-gray-400'
  }

  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700 flex flex-col h-full">
      {/* Header */}
      <div 
        className="p-3 border-b border-slate-700 flex items-center justify-between cursor-pointer hover:bg-slate-700/30 transition-colors"
        onClick={() => setIsMinimized(!isMinimized)}
      >
        <h3 className="font-bold text-white flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-violet-400" />
          Smack Talk
          <span className="text-xs bg-violet-500/20 text-violet-300 px-2 py-0.5 rounded-full">
            {messages.length}
          </span>
        </h3>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isMinimized ? 'rotate-180' : ''}`} />
      </div>

      {!isMinimized && (
        <>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3 min-h-[200px] max-h-[400px]">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader className="w-6 h-6 animate-spin text-violet-400" />
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center py-8">
                <MessageCircle className="w-10 h-10 text-gray-600 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">No messages yet</p>
                <p className="text-gray-600 text-xs">Be the first to talk some trash! 🔥</p>
              </div>
            ) : (
              messages.map((msg) => (
                <div key={msg.id} className="group">
                  <div className="flex items-start gap-2">
                    {/* Rank badge */}
                    {msg.entry_rank && (
                      <div className={`text-xs font-bold ${getRankColor(msg.entry_rank)} min-w-[24px]`}>
                        #{msg.entry_rank}
                      </div>
                    )}
                    {!msg.entry_rank && (
                      <div className="min-w-[24px]" />
                    )}
                    
                    <div className="flex-1 min-w-0">
                      {/* Username and time */}
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm text-white truncate">
                          {msg.username}
                        </span>
                        {msg.entry_score !== null && msg.entry_score > 0 && (
                          <span className="text-xs text-emerald-400">
                            {msg.entry_score.toFixed(1)} pts
                          </span>
                        )}
                        <span className="text-xs text-gray-500">
                          {msg.timeAgo}
                        </span>
                      </div>
                      
                      {/* Message */}
                      <p className="text-gray-300 text-sm break-words">
                        {msg.message}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={sendMessage} className="p-3 border-t border-slate-700">
            {user ? (
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Talk your trash... 🔥"
                  maxLength={500}
                  className="flex-1 px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-gray-500 text-sm focus:border-violet-500 focus:ring-1 focus:ring-violet-500/20 outline-none"
                  disabled={sending}
                />
                <button
                  type="submit"
                  disabled={sending || !newMessage.trim()}
                  className="px-3 py-2 bg-violet-600 hover:bg-violet-500 disabled:bg-slate-700 disabled:text-gray-500 text-white rounded-lg transition-colors"
                >
                  {sending ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
              </div>
            ) : (
              <p className="text-center text-gray-500 text-sm py-2">
                Log in to join the conversation
              </p>
            )}
          </form>
        </>
      )}
    </div>
  )
}
